/** Guest & pre-check-in → "Pre-check-in answers": what each participant answered on the
 *  pre-check-in form — every field the form asks them (standard fields, the operator's
 *  custom fields, tag-category questions), labelled as the form labels it, with the value
 *  as the server words it (`display`); an unanswered field is the em dash.
 *
 *  Read from the booking's pre-check summary (`detail.precheckin`, backend
 *  `apiv2/lib/pre_check_summary.py`): the tour's effective form joined to the stored values,
 *  the lead booker first, then the party. Read-only and the same in view and edit mode —
 *  the stored values, never the draft's. Nothing renders while pre-check-in is off for the
 *  tour, or when the form asks nobody anything. A participant whose form asks nothing (an
 *  empty party list) is left out. */
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingPrecheckinField, BookingPrecheckinSummary } from '@/api/bookings';
import { BlockRule } from '@/features/records/panels/BlockRule';
import { FactGrid, type Fact } from '@/features/records/panels/FactGrid';

/** The list scrolls inside the panel past this height (a party of up to 30). */
const MAX_LIST_PX = 560;

const ROLE_LABEL: Record<'lead' | 'party', string> = { lead: 'Lead booker', party: 'Party member' };

/** The answer as the server words it, or null (the em dash) when unanswered. */
export function answerText(f: BookingPrecheckinField): string | null {
  if (!f.populated) return null;
  return f.display ?? null;
}

/** Long text and the custom note take the whole row, as on the guest form. */
function isLong(f: BookingPrecheckinField): boolean {
  return f.type === 'text' || f.id === 'custom_note';
}

function answerFacts(fields: readonly BookingPrecheckinField[]): Fact[] {
  return fields.map((f) => ({ key: f.id, label: f.label, value: answerText(f), wide: isLong(f) }));
}

export function PrecheckAnswers({
  summary,
  bookingId,
}: {
  summary: BookingPrecheckinSummary | null | undefined;
  /** The opened booking: its participant is named, not linked. */
  bookingId: number;
}) {
  if (!summary?.enabled) return null;
  const participants = summary.participants.filter((p) => p.fields.length > 0);
  if (participants.length === 0) return null;
  return (
    <BlockRule
      icon={<FactCheckOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />}
      title="Pre-check-in answers"
      note="read-only, from the pre-check-in form"
    >
      <Box
        component="ul"
        aria-label="Pre-check-in answers"
        data-precheck-answers
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          maxHeight: MAX_LIST_PX,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {participants.map((p) => {
          const own = p.gtu === bookingId;
          const name = p.name?.trim() || `Booking #${p.gtu}`;
          const status = p.checked_in ? 'Complete' : p.skipped ? 'Skipped' : null;
          return (
            <Box
              component="li"
              key={p.gtu}
              aria-label={name}
              data-precheck-participant={p.gtu}
              sx={{ px: 1.25, pt: 0.75, '& + &': { borderTop: 1, borderColor: 'divider' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                {own ? (
                  <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {name}
                  </Typography>
                ) : (
                  <Link
                    component={RouterLink}
                    to={`/bookings/${p.gtu}`}
                    underline="hover"
                    variant="body2"
                    sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
                  >
                    {name}
                  </Link>
                )}
                <Typography variant="caption" color="text.secondary">
                  {[ROLE_LABEL[p.role], own ? 'this booking' : `Booking #${p.gtu}`, status].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <FactGrid facts={answerFacts(p.fields)} />
            </Box>
          );
        })}
      </Box>
    </BlockRule>
  );
}
